import React, { useState, useEffect, useRef } from 'react';
import {
  Editor
} from 'slate';
import {
  useSlateStatic,
  ReactEditor
} from 'slate-react';

import application from 'application';

import internalEventCodes from 'event-system/codes/internal';

import { series } from 'utilities';

import Helpers from '../helpers';
import {
  determineAlignmentType,
  buildBackgroundProperties
} from './utilities';

import HelptapColorPicker from 'generics/components/color-picker';

import nodeControls from './control-types';

import ExpandableNodeActionMenuItem from './menu-item';
import FourSides from './four-sides';
import WidthHeightInput from './width-height-input';
import AlignmentMenuItem from './alignment-menu-item';
import AltTextInput from './alt-text-input';
import ColumnsCountInput from './columns-count-input';
import ColumnSpacingInput from './column-spacing-input';
import FileURLInput from './file-url-input';
import BoxShadowInput from './box-shadow-input';
import BackgroundInput from './background-input';
import TransitionInputReact from './transition-input/react';
import { getDefaultTransition } from './transition-input';
import OpacityInputReact from './opacity-input/react';
import TransformInput from './transform-input';
import NativeMoreNodeActionsMenuReactWrapper from '../more-node-actions-menu/react';
import TableRowsAndColumnsCountInput from './table-rows-and-columns-count-input';
import GallerySettingsReactWrapper from './gallery-settings/react';
import CarouselSettingsReactWrapper from './carousel-settings/react';
import { tryRenderHeaderSettings } from './site-nav-menu-items';
import { surfaceAttributes } from '../helpers/utilities/site-nav/styles';
import { tryRenderIconSettings } from './icon-menu-items';
import { tryRenderLayoutItemSettingsMenuItem, tryRenderLayoutSettingsMenuItem } from './layout-menu-items';

import { ReactComponent as PaddingControlsIcon } from 'assets/images/padding-controls-icon.svg';
import { ReactComponent as MarginControlsIcon } from 'assets/images/margin-controls-icon.svg';
import { ReactComponent as RadiusControlsIcon } from 'assets/images/radius-controls-icon.svg';
import { ReactComponent as BorderControlsIcon } from 'assets/images/border-controls-icon.svg';
import { ReactComponent as NodeMenuBackgroundColorIcon } from 'assets/images/node-menu-background-color-icon.svg';
import { ReactComponent as NodeMenuTextColorIcon } from 'assets/images/node-menu-text-color-icon.svg';
import { ReactComponent as NodeMenuURLIcon } from 'assets/images/link-toggle-icon.svg';
import { ReactComponent as NodeMenuWidthHeightIcon } from 'assets/images/node-menu-width-height-icon.svg';
import { ReactComponent as NodeMenuAlignmentIcon } from 'assets/images/node-menu-alignment-icon.svg';
import { ReactComponent as NodeMenuAltTextInputIcon } from 'assets/images/node-menu-alt-text-input-icon.svg';
import { ReactComponent as TableEditIcon } from 'assets/images/table-edit-icon.svg';
import { ReactComponent as BoxShadowIcon } from 'assets/images/box-shadow-icon.svg';
import { ReactComponent as ColumnSpacingIcon } from 'assets/images/column-spacing-icon.svg';
import { ReactComponent as TransitionIcon } from 'assets/images/animation-controls-icon.svg';
import { ReactComponent as OpacityIcon } from 'assets/images/eye-open-icon.svg';
import { ReactComponent as TransformIcon } from 'assets/images/transform-controls-icon.svg';

import './index.scss';

function NodeMenu(props) {

  const editor = useSlateStatic();
  const elementPath = props.elementPath;
  const customNodeControls = props.customNodeControls || [];
  const disabledNodeControls = props.disabledNodeControls || [];

  const node = Editor.node(editor, elementPath)[0];

  const menuReference = useRef(null);

  const [rootState, setRootState] = useState({
    activeMenuItemName: null
  });

  const [url, setURL] = useState(node.url || '');

  useEffect(function registerForEvents() {

    focusMenuAndReleaseEditorFocus();

    const eventRegisterers = [
      registerRepositionNodeMenuEventHandler
    ];

    const eventDeregisterers = series(eventRegisterers);

    return function unregisterEventHandlers() {
      series(eventDeregisterers);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(menuReferenceChanged, [menuReference.current]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(elementPathChanged, [props.elementPath]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(activeMenuItemNameChanged, [rootState.activeMenuItemName]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(selectionChanged, [editor.selection]);

  function focusMenuAndReleaseEditorFocus() {

    menuReference.current.focus();

    // Separate selection from focus. editor.selection is kept intact because the
    // AI assistant needs the edited block to stay selected (selection is reported
    // through onSelectChange). But while slate-react believes the editor is
    // focused, it re-syncs the DOM selection back into the editor on every
    // transform, which steals focus away from these node-menu inputs after the
    // first keypress (intermittent, and more likely on nested blocks because of a
    // render/timing race). ReactEditor.blur clears slate's focus flag without
    // touching editor.selection, so menu edits keep applying live while the block
    // stays selected. Do not remove.
    ReactEditor.blur(editor);

  }

  function registerRepositionNodeMenuEventHandler() {

    function repositionNodeMenu() {
      positionToolbar();
    }

    application.eventSystem.internal.on(internalEventCodes.REPOSITION_EDITOR_NODE_MENU, repositionNodeMenu);

    return function unregisterRepositionNodeMenuEventHandler() {

      application.eventSystem.internal.off(internalEventCodes.REPOSITION_EDITOR_NODE_MENU, repositionNodeMenu);

    };

  }

  function menuReferenceChanged() {
    positionToolbar();
  }

  function elementPathChanged() {

    positionToolbar();

  }

  function activeMenuItemNameChanged() {

    positionToolbar();

  }

  function selectionChanged() {

    positionToolbar();

  }

  function positionToolbar() {

    return props.positionToolbar();

  }

  function toggleActiveMenuItem(menuItemName) {

    let activeMenuItemName = menuItemName;

    if (rootState.activeMenuItemName === activeMenuItemName) {
      activeMenuItemName = null;
    }

    setRootState({
      ...rootState,
      activeMenuItemName
    });

  }

  function handleTabKeyEvent(event) {

    event.preventDefault();
    event.stopPropagation();

    if (event.shiftKey === false) {
      Helpers.indentation.indent(editor, elementPath);
    } else {
      Helpers.indentation.unindent(editor, elementPath);
    }

  }

  function handleDeleteKeyEvent(event) {

    event.preventDefault();
    event.stopPropagation();

    Helpers.deleteNodeByPath(editor, elementPath);
    props.onClose();

  }

  function handleKeyDownEvent(event) {

    if (event.target !== menuReference.current) {
      return;
    }

    const key = event.key;

    if (key === 'Tab') {
      handleTabKeyEvent(event);
    } else if (key === 'Delete' || key === 'Backspace') {
      handleDeleteKeyEvent(event);
    }

  }

  function renderPaddingInputContent() {

    const paddingControlProperties = {
      values: node.padding,
      onChange(values) {
        Helpers.setAttributes(editor, { padding: values }, elementPath);
        positionToolbar();
      }
    };

    return <FourSides {...paddingControlProperties} />;

  }

  function renderPaddingMenuItem() {

    const paddingMenuItemProperties = {
      Icon: PaddingControlsIcon,
      name: 'Padding',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderPaddingInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...paddingMenuItemProperties} />;

  }

  function renderMarginInputContent() {

    const marginControlProperties = {
      values: node.margin,
      onChange(values) {
        Helpers.setAttributes(editor, { margin: values }, elementPath);
      }
    };

    return <FourSides {...marginControlProperties} />;

  }

  function renderMarginMenuItem() {

    const marginMenuItemProperties = {
      Icon: MarginControlsIcon,
      name: 'Margin',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderMarginInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...marginMenuItemProperties} />;

  }

  function renderRadiusInputContent() {

    const radiusControlProperties = {
      values: node.radius,
      onChange(values) {
        Helpers.setAttributes(editor, { radius: values }, elementPath);
      }
    };

    return <FourSides {...radiusControlProperties} />;

  }

  function renderRadiusMenuItem() {

    const radiusMenuItemProperties = {
      Icon: RadiusControlsIcon,
      name: 'Radius',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderRadiusInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...radiusMenuItemProperties} />;

  }

  function renderBorderInputContent() {

    const border = node.border;
    let width = [0, 0, 0, 0];
    let color = '#000';

    if (typeof border !== 'undefined' && border !== null) {
      width = border.width;
      color = border.color;
    }

    const borderControlProperties = {
      values: width,
      onChange(values) {
        Helpers.setAttributes(editor, surfaceAttributes(node, {
          border: {
            width: values,
            style: 'solid',
            color: color
          }
        }), elementPath);
      }
    };

    const colorPickerProperties = {
      color: color,
      onChange(borderColor) {
        Helpers.setAttributes(editor, surfaceAttributes(node, {
          border: {
            width: width,
            style: 'solid',
            color: borderColor
          }
        }), elementPath);

      }
    };

    return <div className="border-control-container">

      <FourSides {...borderControlProperties} />

      <div className="node-properties-menu-color-picker-wrapper">
        <HelptapColorPicker {...colorPickerProperties} />
      </div>

    </div>;

  }

  function renderBorderMenuItem() {

    const borderMenuItemProperties = {
      Icon: BorderControlsIcon,
      name: 'Border',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderBorderInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...borderMenuItemProperties} />;

  }

  function renderTextColorInputContent() {

    const colorPickerProperties = {
      color: node.textColor,
      onChange(textColor) {
        Helpers.setAttributes(editor, surfaceAttributes(node, {
          textColor
        }), elementPath);
      }
    };

    return <div className="node-properties-menu-color-picker-wrapper">
      <HelptapColorPicker {...colorPickerProperties} />
    </div>;
  }

  function tryRenderTextColorMenuItem() {

    if (disabledNodeControls.indexOf(nodeControls.TEXT_COLOR) !== -1) {
      return;
    }

    const textColorMenuItemProperties = {
      Icon: NodeMenuTextColorIcon,
      name: 'Text',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderTextColorInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...textColorMenuItemProperties} />;

  }

  function renderBoxShadowInputInputContent() {

    const boxShadowProperties = {
      boxShadow: node.boxShadow,
      onChange(boxShadow) {
        Helpers.setAttributes(editor, surfaceAttributes(node, {
          boxShadow
        }), elementPath);
      }
    };

    return <BoxShadowInput {...boxShadowProperties} />;
  }

  function tryRenderBoxShadowInputMenuItem() {

    const boxShadowMenuItemProperties = {
      Icon: BoxShadowIcon,
      name: 'Shadow',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderBoxShadowInputInputContent,
      positionToolbar,
      scrollTarget: '.box-shadow-number-inputs-container',
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...boxShadowMenuItemProperties} />;

  }

  function renderBackgroundInputContent() {

    const backgroundInputProperties = {
      application,
      mediaUploader: props.mediaUploader,
      backgroundColor: node.backgroundColor,
      backgroundImage: node.backgroundImage,
      backgroundPosition: node.backgroundPosition,
      backgroundSize: node.backgroundSize,
      onChange(background) {
        Helpers.setAttributes(editor, surfaceAttributes(node, {
          ...background
        }), elementPath);
      }
    };

    return <BackgroundInput {...backgroundInputProperties} />;
  }

  function renderFormButtonBackgroundInputContent() {

    const properties = (node.properties || {});
    const backgroundInputProperties = {
      application,
      mediaUploader: props.mediaUploader,
      ...buildBackgroundProperties(properties.submitButtonBackground || {}),
      onChange(background) {
        Helpers.form.setButtonBackground(editor, node, elementPath, {
          color: background.backgroundColor,
          image: background.backgroundImage,
          position: background.backgroundPosition,
          size: background.backgroundSize
        });
      }
    };

    return <BackgroundInput {...backgroundInputProperties} />;
  }

  function renderFormButtonHoverBackgroundInputContent() {

    const properties = (node.properties || {});
    const backgroundInputProperties = {
      application,
      mediaUploader: props.mediaUploader,
      ...buildBackgroundProperties(properties.submitButtonHoverBackground || {}),
      onChange(background) {
        Helpers.form.setButtonHoverBackground(editor, node, elementPath, {
          color: background.backgroundColor,
          image: background.backgroundImage,
          position: background.backgroundPosition,
          size: background.backgroundSize
        });
      }
    };

    return <BackgroundInput {...backgroundInputProperties} />;
  }

  function renderFormBackgroundInputContent() {

    const properties = (node.properties || {});
    const backgroundInputProperties = {
      application,
      mediaUploader: props.mediaUploader,
      backgroundColor: properties.backgroundColor,
      backgroundImage: properties.backgroundImage,
      backgroundPosition: properties.backgroundPosition,
      onChange(background) {
        Helpers.form.setBackground(editor, node, elementPath, background);
      }
    };

    return <BackgroundInput {...backgroundInputProperties} />;
  }

  function renderBackgroundMenuItem() {

    if (customNodeControls.indexOf(nodeControls.FORM_BACKGROUND_INPUT) !== -1) {
      return;
    }

    const backgroundColorMenuItemProperties = {
      Icon: NodeMenuBackgroundColorIcon,
      name: 'Background',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderBackgroundInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...backgroundColorMenuItemProperties} />;

  }

  function tryRenderFormButtonBackgroundMenuItem() {

    if (customNodeControls.indexOf(nodeControls.FORM_BACKGROUND_INPUT) === -1) {
      return;
    }

    const backgroundColorMenuItemProperties = {
      Icon: NodeMenuBackgroundColorIcon,
      name: 'Button background',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderFormButtonBackgroundInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...backgroundColorMenuItemProperties} />;

  }

  function tryRenderFormButtonHoverBackgroundMenuItem() {

    if (customNodeControls.indexOf(nodeControls.FORM_BACKGROUND_INPUT) === -1) {
      return;
    }

    const backgroundColorMenuItemProperties = {
      Icon: NodeMenuBackgroundColorIcon,
      name: 'Button hover',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderFormButtonHoverBackgroundInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...backgroundColorMenuItemProperties} />;

  }

  function tryRenderFormBackgroundMenuItem() {

    if (customNodeControls.indexOf(nodeControls.FORM_BACKGROUND_INPUT) === -1) {
      return;
    }

    const backgroundColorMenuItemProperties = {
      Icon: NodeMenuBackgroundColorIcon,
      name: 'Form Background',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderFormBackgroundInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...backgroundColorMenuItemProperties} />;

  }

  function renderHoverBackgroundColorInputContent() {

    var hover = node.hover || {};

    const colorPickerProperties = {
      color: hover.backgroundColor,
      onChange(backgroundColor) {
        Helpers.setAttributes(editor, {
          hover: {
            ...hover,
            backgroundColor
          }
        }, elementPath);
      }
    };

    return <div className="node-properties-menu-color-picker-wrapper">
      <HelptapColorPicker {...colorPickerProperties} />
    </div>;
  }

  function tryRenderHoverBackgroundColorMenuItem() {

    if (customNodeControls.indexOf(nodeControls.HOVER_BACKGROUND_COLOR) === -1) {
      return;
    }

    const backgroundColorMenuItemProperties = {
      Icon: NodeMenuBackgroundColorIcon,
      name: 'Hover Background',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderHoverBackgroundColorInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...backgroundColorMenuItemProperties} />;

  }

  function renderHoverTextColorInputContent() {

    var hover = node.hover || {};

    const colorPickerProperties = {
      color: hover.textColor,
      onChange(textColor) {
        Helpers.setAttributes(editor, {
          hover: {
            ...hover,
            textColor
          }
        }, elementPath);
      }
    };

    return <div className="node-properties-menu-color-picker-wrapper">
      <HelptapColorPicker {...colorPickerProperties} />
    </div>;
  }

  function tryRenderHoverTextColorMenuItem() {

    if (customNodeControls.indexOf(nodeControls.HOVER_TEXT_COLOR) === -1) {
      return;
    }

    const backgroundColorMenuItemProperties = {
      Icon: NodeMenuTextColorIcon,
      name: 'Hover Text',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderHoverTextColorInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...backgroundColorMenuItemProperties} />;

  }

  function renderURLInputContent() {

    const urlControlProperties = {
      type: 'text',
      value: url,
      placeholder: 'URL',
      className: 'url-input-control',
      onChange(event) {

        setURL(event.target.value);
        Helpers.setAttributes(editor, { url: event.target.value }, elementPath);

      }
    };

    return <input {...urlControlProperties} />;

  }

  function tryRenderURLMenuItem() {

    if (customNodeControls.indexOf(nodeControls.URL_INPUT) === -1) {
      return;
    }

    const urlMenuItemProperties = {
      className: 'url-menu-item',
      Icon: NodeMenuURLIcon,
      name: 'URL',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderURLInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...urlMenuItemProperties} />;

  }

  function renderFileURLInputContent() {

    const urlControlProperties = {
      editor,
      node,
      elementPath
    };

    return <FileURLInput {...urlControlProperties} />;

  }

  function tryRenderFileURLMenuItem() {

    if (customNodeControls.indexOf(nodeControls.FILE_URL_INPUT) === -1) {
      return;
    }

    const urlMenuItemProperties = {
      Icon: NodeMenuURLIcon,
      name: 'File URL',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderFileURLInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...urlMenuItemProperties} />;

  }

  function renderWidthHeightInputContent() {

    const widthHeightControlProperties = {
      values: {
        width: node.width,
        height: node.height
      },
      onChange(values) {
        Helpers.setAttributes(editor, values, elementPath);
      }
    };

    return <WidthHeightInput {...widthHeightControlProperties} />;

  }

  function renderWidthHeightMenuItem() {

    const widthHeightMenuItemProperties = {
      Icon: NodeMenuWidthHeightIcon,
      name: 'Width / Height',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderWidthHeightInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...widthHeightMenuItemProperties} />;

  }

  function renderAlignmentInputContent() {

    const alignmentControlProperties = {
      values: {
        marginLeft: node.marginLeft,
        marginRight: node.marginRight
      },
      onChange(values) {
        if (node.type === 'image') {
          Helpers.setAttributes(editor, {
            alignment: determineAlignmentType(values)
          }, elementPath);
        }
        Helpers.setAttributes(editor, values, elementPath);
      }
    };

    return <AlignmentMenuItem {...alignmentControlProperties} />;

  }

  function tryRenderAlignmentMenuItem() {

    if (customNodeControls.indexOf(nodeControls.ALIGNMENT) === -1) {
      return;
    }

    const alignmentMenuItemProperties = {
      Icon: NodeMenuAlignmentIcon,
      name: 'Alignment',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderAlignmentInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...alignmentMenuItemProperties} />;

  }

  function renderAltTextInputContent() {

    const altTextInputProperties = {
      value: node.attributes.altText || '',
      onChange(altText) {
        Helpers.setAttributes(editor, { attributes: { ...(node.attributes || {}), altText } }, elementPath);
      }
    };

    return <AltTextInput {...altTextInputProperties} />;

  }

  function tryRenderAltTextInputMenuItem() {

    if (customNodeControls.indexOf(nodeControls.ALT_TEXT_INPUT) === -1) {
      return;
    }

    const alignmentMenuItemProperties = {
      Icon: NodeMenuAltTextInputIcon,
      name: 'Alt Text',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderAltTextInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...alignmentMenuItemProperties} />;

  }

  function renderColumnsCountInputContent() {

    const columnsCountInputProperties = {
      value: node.children.length,
      onChange(columnsCount) {
        Helpers.multipleColumns.updateColumnsCount(editor, elementPath, columnsCount);
      }
    };

    return <ColumnsCountInput {...columnsCountInputProperties} />;

  }

  function tryRenderColumnsCountInputMenuItem() {

    if (customNodeControls.indexOf(nodeControls.COLUMNS_COUNT_INPUT) === -1) {
      return;
    }

    const alignmentMenuItemProperties = {
      Icon: TableEditIcon,
      name: 'Columns count',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderColumnsCountInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...alignmentMenuItemProperties} />;

  }

  function renderColumnSpacingInputContent() {

    const columnsCountInputProperties = {
      value: node.properties?.columnSpacing || 5,
      onChange(columnsCount) {
        Helpers.multipleColumns.updateColumnSpacing(editor, elementPath, columnsCount);
      }
    };

    return <ColumnSpacingInput {...columnsCountInputProperties} />;

  }

  function tryRenderColumnSpacingInputMenuItem() {

    if (customNodeControls.indexOf(nodeControls.COLUMN_SPACING_INPUT) === -1) {
      return;
    }

    const alignmentMenuItemProperties = {
      Icon: ColumnSpacingIcon,
      name: 'Column spacing',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderColumnSpacingInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...alignmentMenuItemProperties} />;

  }

  function renderTableRowsAndColumnsCountInputContent() {

    const tableRowsCountInputProperties = {
      values: [node.children.length, node.children[0].children.length],
      onRowsCountChange(rowsCount) {
        Helpers.table.updateRows(editor, node, elementPath, rowsCount);
      },
      onColumnsCountChange(columnsCount) {
        Helpers.table.updateColumns(editor, node, elementPath, columnsCount);
      }
    };

    return <TableRowsAndColumnsCountInput {...tableRowsCountInputProperties} />;

  }

  function tryRenderTableRowsAndColumnsCountInputMenuItem() {

    if (customNodeControls.indexOf(nodeControls.TABLE_ROWS_AND_COLUMNS_COUNT_INPUT) === -1) {
      return;
    }

    const alignmentMenuItemProperties = {
      Icon: TableEditIcon,
      name: 'Rows / Columns',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderTableRowsAndColumnsCountInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...alignmentMenuItemProperties} />;

  }

  function renderRowPaddingInputContent() {

    const paddingControlProperties = {
      values: node.children[0].children[0].padding,
      onChange(values) {
        Helpers.table.setRowPadding(editor, values, elementPath);
        positionToolbar();
      }
    };

    return <FourSides {...paddingControlProperties} />;

  }

  function tryRenderRowPaddingMenuItem() {

    if (customNodeControls.indexOf(nodeControls.TABLE_ROWS_PADDING_INPUT) === -1) {
      return;
    }

    const paddingMenuItemProperties = {
      Icon: PaddingControlsIcon,
      name: 'Cell padding',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderRowPaddingInputContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...paddingMenuItemProperties} />;

  }

  function getTransitionValue() {

    var transition = node.transition;

    if (transition === void 0 || transition === null) {
      return getDefaultTransition();
    }

    return transition;

  }

  function renderTransitionInputContent() {

    var transitionInputProperties = {
      transition: getTransitionValue(),
      onChange: function handleTransitionChange(transition) {
        Helpers.setAttributes(editor, { transition }, elementPath);
      }
    };

    return <TransitionInputReact {...transitionInputProperties} />;

  }

  function tryRenderTransitionInputMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var transitionMenuItemProperties = {
      Icon: TransitionIcon,
      name: 'Transition',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderTransitionInputContent,
      positionToolbar: positionToolbar,
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...transitionMenuItemProperties} />;

  }

  function renderHoverBackgroundInputContent() {

    var hover = node.hover || {};
    var hoverBackground = hover.background || {};

    var backgroundInputProperties = {
      application: application,
      mediaUploader: props.mediaUploader,
      ...buildBackgroundProperties(hoverBackground),
      onChange: function handleHoverBackgroundChange(background) {
        var updatedBackground = {
          color: background.backgroundColor,
          image: background.backgroundImage,
          position: background.backgroundPosition,
          size: background.backgroundSize
        };
        Helpers.setAttributes(editor, {
          hover: {
            ...hover,
            background: updatedBackground
          }
        }, elementPath);
      }
    };

    return <BackgroundInput {...backgroundInputProperties} />;

  }

  function tryRenderHoverBackgroundInputMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var hoverBackgroundMenuItemProperties = {
      Icon: NodeMenuBackgroundColorIcon,
      name: 'Hover Background',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderHoverBackgroundInputContent,
      positionToolbar: positionToolbar,
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...hoverBackgroundMenuItemProperties} />;

  }

  function renderHoverShadowInputContent() {

    var hover = node.hover || {};
    var hoverShadow = hover.boxShadow || null;

    var boxShadowProperties = {
      boxShadow: hoverShadow,
      onChange: function handleHoverShadowChange(boxShadow) {
        Helpers.setAttributes(editor, {
          hover: {
            ...hover,
            boxShadow: boxShadow
          }
        }, elementPath);
      }
    };

    return <BoxShadowInput {...boxShadowProperties} />;

  }

  function tryRenderHoverShadowInputMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var hoverShadowMenuItemProperties = {
      Icon: BoxShadowIcon,
      name: 'Hover Shadow',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderHoverShadowInputContent,
      positionToolbar: positionToolbar,
      scrollTarget: '.box-shadow-number-inputs-container',
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...hoverShadowMenuItemProperties} />;

  }

  function renderOpacityInputContent() {

    var opacity = node.opacity;

    if (opacity === void 0 || opacity === null) {
      opacity = 1;
    }

    var opacityInputProperties = {
      opacity: opacity,
      onChange: function handleOpacityChange(value) {
        Helpers.setAttributes(editor, { opacity: value }, elementPath);
      }
    };

    return <OpacityInputReact {...opacityInputProperties} />;

  }

  function tryRenderOpacityMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var opacityMenuItemProperties = {
      Icon: OpacityIcon,
      name: 'Opacity',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderOpacityInputContent,
      positionToolbar: positionToolbar,
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...opacityMenuItemProperties} />;

  }

  function renderHoverOpacityInputContent() {

    var hover = node.hover || {};
    var opacity = hover.opacity;

    if (opacity === void 0 || opacity === null) {
      opacity = 1;
    }

    var opacityInputProperties = {
      opacity: opacity,
      onChange: function handleHoverOpacityChange(value) {
        Helpers.setAttributes(editor, {
          hover: {
            ...hover,
            opacity: value
          }
        }, elementPath);
      }
    };

    return <OpacityInputReact {...opacityInputProperties} />;

  }

  function tryRenderHoverOpacityMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var hoverOpacityMenuItemProperties = {
      Icon: OpacityIcon,
      name: 'Hover Opacity',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderHoverOpacityInputContent,
      positionToolbar: positionToolbar,
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...hoverOpacityMenuItemProperties} />;

  }

  function renderHoverRadiusInputContent() {

    var hover = node.hover || {};
    var radius = hover.radius;

    var radiusControlProperties = {
      values: radius,
      onChange: function handleHoverRadiusChange(values) {
        Helpers.setAttributes(editor, {
          hover: {
            ...hover,
            radius: values
          }
        }, elementPath);
      }
    };

    return <FourSides {...radiusControlProperties} />;

  }

  function tryRenderHoverRadiusMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var hoverRadiusMenuItemProperties = {
      Icon: RadiusControlsIcon,
      name: 'Hover Radius',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderHoverRadiusInputContent,
      positionToolbar: positionToolbar,
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...hoverRadiusMenuItemProperties} />;

  }

  function renderGallerySettingsContent() {

    const gallerySettingsProperties = {
      settings: Helpers.gallery.getSettings(node),
      onChange(settings) {
        Helpers.gallery.updateSettings(editor, elementPath, settings);
        positionToolbar();
      }
    };

    return <GallerySettingsReactWrapper {...gallerySettingsProperties} />;

  }

  function tryRenderGallerySettingsMenuItem() {

    if (customNodeControls.indexOf(nodeControls.GALLERY_SETTINGS) === -1) {
      return;
    }

    const gallerySettingsMenuItemProperties = {
      Icon: TableEditIcon,
      name: 'Gallery settings',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderGallerySettingsContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...gallerySettingsMenuItemProperties} />;

  }

  function renderCarouselSettingsContent() {

    const carouselSettingsProperties = {
      settings: Helpers.carousel.getSettings(node),
      onChange(settings) {
        Helpers.carousel.updateSettings(editor, elementPath, settings);
        positionToolbar();
      }
    };

    return <CarouselSettingsReactWrapper {...carouselSettingsProperties} />;

  }

  function tryRenderCarouselSettingsMenuItem() {

    if (customNodeControls.indexOf(nodeControls.CAROUSEL_SETTINGS) === -1) {
      return;
    }

    const carouselSettingsMenuItemProperties = {
      Icon: TableEditIcon,
      name: 'Carousel settings',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderCarouselSettingsContent,
      positionToolbar,
      toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...carouselSettingsMenuItemProperties} />;

  }

  function renderTransformInputContent() {

    var hover = node.hover || {};

    var transformInputProperties = {
      hover: hover,
      onChange: function handleTransformPropertyChange(property, value) {
        var updatedHover = {};
        var hoverKeys = Object.keys(hover);
        for (var i = 0; i < hoverKeys.length; i++) {
          updatedHover[hoverKeys[i]] = hover[hoverKeys[i]];
        }
        if (value === null) {
          delete updatedHover[property];
        } else {
          updatedHover[property] = value;
        }
        Helpers.setAttributes(editor, { hover: updatedHover }, elementPath);
      }
    };

    return <TransformInput {...transformInputProperties} />;

  }

  function tryRenderTransformInputMenuItem() {

    if (props.hoverControlsEnabled !== true) {
      return;
    }

    var transformMenuItemProperties = {
      Icon: TransformIcon,
      name: 'Hover Transform',
      activeMenuItemName: rootState.activeMenuItemName,
      contentRenderer: renderTransformInputContent,
      positionToolbar: positionToolbar,
      toggleActiveMenuItem: toggleActiveMenuItem
    };

    return <ExpandableNodeActionMenuItem {...transformMenuItemProperties} />;

  }

  function buildLayoutMenu() {

    return {
      editor,
      elementPath,
      customNodeControls,
      onClose: props.onClose,
      activeMenuItemName: rootState.activeMenuItemName,
      positionToolbar,
      toggleActiveMenuItem
    };

  }

  function renderMenuItems() {

    return <div className="node-properties-menu-list">
      {tryRenderHeaderSettings(buildLayoutMenu())}
      {renderPaddingMenuItem()}
      {renderMarginMenuItem()}
      {renderRadiusMenuItem()}
      {renderBorderMenuItem()}
      {tryRenderTextColorMenuItem()}
      {renderBackgroundMenuItem()}
      {tryRenderFormButtonBackgroundMenuItem()}
      {tryRenderFormButtonHoverBackgroundMenuItem()}
      {tryRenderFormBackgroundMenuItem()}
      {tryRenderHoverBackgroundColorMenuItem()}
      {tryRenderHoverTextColorMenuItem()}
      {tryRenderBoxShadowInputMenuItem()}
      {tryRenderURLMenuItem()}
      {renderWidthHeightMenuItem()}
      {tryRenderAlignmentMenuItem()}
      {tryRenderAltTextInputMenuItem()}
      {tryRenderColumnsCountInputMenuItem()}
      {tryRenderColumnSpacingInputMenuItem()}
      {tryRenderGallerySettingsMenuItem()}
      {tryRenderCarouselSettingsMenuItem()}
      {tryRenderIconSettings(buildLayoutMenu())}
      {tryRenderLayoutSettingsMenuItem(buildLayoutMenu())}
      {tryRenderLayoutItemSettingsMenuItem(buildLayoutMenu())}
      {tryRenderTableRowsAndColumnsCountInputMenuItem()}
      {tryRenderFileURLMenuItem()}
      {tryRenderRowPaddingMenuItem()}
      {tryRenderTransitionInputMenuItem()}
      {tryRenderOpacityMenuItem()}
      {tryRenderHoverBackgroundInputMenuItem()}
      {tryRenderHoverShadowInputMenuItem()}
      {tryRenderHoverOpacityMenuItem()}
      {tryRenderHoverRadiusMenuItem()}
      {tryRenderTransformInputMenuItem()}
    </div>;

  }

  function renderMain() {

    const mainAttributes = {
      className: 'node-menu-main editor-tool-container'
    };

    const menuContainerAttributes = {
      className: 'node-menu-container hide-scrollbar',
      ref: menuReference,
      tabIndex: -1,
      onKeyDown(event) {
        handleKeyDownEvent(event);
      }
    };

    return <div {...mainAttributes}>
      <div {...menuContainerAttributes}>
        {renderMenuItems()}
      </div>
    </div>;

  }

  return renderMain();

}

NodeMenu.callers = {
  MOUSE: 'MOUSE',
  KEYBOARD: 'KEYBOARD'
};

export default NodeMenu;
